import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

/**
 * Install prompt that appears the first time a user visits on mobile.
 * - On Android/Chrome we capture the `beforeinstallprompt` event and offer
 *   a native install button.
 * - On iOS Safari (which has no event) we show a small how-to sheet
 *   that walks the learner through "Share → Add to Home Screen".
 *
 * The prompt is dismissible and remembers the choice in localStorage so
 * it never nags the same user twice.
 */
export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;

        // Never show if the app is already installed / running standalone.
        const standalone =
            window.matchMedia?.("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (standalone) return;

        // Respect the user's earlier dismissal (7-day cool-down).
        const dismissedAt = Number(localStorage.getItem("lur_install_dismissed_at") || 0);
        if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;

        const ua = window.navigator.userAgent || "";
        const iOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
        setIsIOS(iOS);

        const handler = (e) => {
            e.preventDefault();
            setDeferred(e);
            setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", handler);

        // iOS Safari has no beforeinstallprompt — surface the manual sheet
        // after a short delay so it doesn't fight the first paint.
        if (iOS) {
            const t = setTimeout(() => setVisible(true), 2500);
            return () => {
                clearTimeout(t);
                window.removeEventListener("beforeinstallprompt", handler);
            };
        }

        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    const dismiss = () => {
        localStorage.setItem("lur_install_dismissed_at", String(Date.now()));
        setVisible(false);
    };

    const install = async () => {
        if (!deferred) return;
        try {
            await deferred.prompt();
            await deferred.userChoice;
        } catch {
            /* noop */
        }
        dismiss();
    };

    if (!visible) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-[380px] z-[60] card-rw p-5 shadow-lg"
            style={{ borderTop: "4px solid #2D5D4E", background: "white" }}
            data-testid="install-prompt"
        >
            <button
                onClick={dismiss}
                aria-label="Dismiss"
                data-testid="install-prompt-dismiss"
                className="absolute top-3 right-3 text-[#819888] hover:text-[#1C2321]"
            >
                <X className="w-4 h-4" strokeWidth={1.8} />
            </button>

            <div className="flex items-start gap-3 pr-6">
                <div className="w-10 h-10 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-[#2D5D4E]" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3
                        className="text-lg font-medium mb-1 text-[#1C2321]"
                        style={{ fontFamily: "Outfit" }}
                    >
                        Add Lift Up to your home screen
                    </h3>
                    {isIOS && !deferred ? (
                        <p className="text-sm text-[#515B57] leading-relaxed">
                            Tap <Share className="inline w-4 h-4 -mt-0.5" strokeWidth={1.8} /> Share in
                            Safari, then <strong>Add to Home Screen</strong>. Opens like a real app —
                            no App Store needed.
                        </p>
                    ) : (
                        <p className="text-sm text-[#515B57] leading-relaxed">
                            Practice faster from your phone. No App Store, no extra sign-in.
                        </p>
                    )}
                </div>
            </div>

            {deferred && (
                <div className="mt-4 flex gap-2 justify-end">
                    <button
                        onClick={dismiss}
                        data-testid="install-prompt-later"
                        className="px-3 py-2 rounded-lg text-sm text-[#515B57] hover:text-[#1C2321]"
                    >
                        Not now
                    </button>
                    <button
                        onClick={install}
                        data-testid="install-prompt-install"
                        className="px-4 py-2 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-sm font-medium hover:bg-[#1F4237]"
                    >
                        Install
                    </button>
                </div>
            )}
        </div>
    );
}
